import { ReactNode, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import InfoTip from '../../../components/novarium/InfoTip'
import { formatCount } from '../operatingRoomFormat'

/** Cuántas filas se ven antes de «Ver todos (N)». */
export const TABLE_TOP_ROWS = 10

/** Ancho mínimo de la columna del nombre: debajo de lo que suman las demás, la tabla se desplaza dentro de su caja. */
const NAME_COLUMN_MIN_WIDTH = 208

export interface OperatingRoomTableColumn<T> {
  key: string
  header: string
  /** Fórmula o criterio de la columna (ⓘ, a demanda). */
  hint?: string
  /** Datos cortos (números, tiempos): centrados, con la cabecera centrada. */
  centered?: boolean
  /** Ancho de la columna, en px: lo que mide su cabecera con el ⓘ y el orden. Todas las tablas de la pantalla alinean sus columnas. */
  width?: number
  /** Con qué se ordena la columna; sin él la columna no se ordena. Sin dato, siempre al final. */
  sortValue?: (row: T) => number | string | null
  cell: (row: T) => ReactNode
  /** Celda del pie (totales); sin ninguna columna con pie, la tabla no lleva pie. */
  footer?: ReactNode
}

interface Sort {
  key: string
  order: 1 | -1
}

interface OperatingRoomTableProps<T> {
  /** Qué es la tabla, para el lector de pantalla (`<caption>`): el título que se ve la nombra a la vista. */
  caption: string
  rows: T[]
  rowKey: (row: T, index: number) => string
  /** La primera columna es el nombre de la fila (encabezado de fila). */
  columns: OperatingRoomTableColumn<T>[]
  /** Orden inicial: lo que más pesa, primero (como llegan del backend). */
  defaultSort: Sort
  footerLabel?: string
  /** Si el backend mandó solo las primeras filas de `totalRows`, para no aparentar que la lista está completa. */
  totalRows?: number
  emptyMessage: string
}

function compare(left: number | string | null, right: number | string | null, order: 1 | -1): number {
  // Sin dato, siempre al final
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  if (typeof left === 'string' || typeof right === 'string') return order * String(left).localeCompare(String(right), 'es')
  return order * (left - right)
}

/**
 * Tabla de una pestaña de quirófano (completa en memoria): cabeceras ordenables con el teclado (un botón por columna, con
 * `aria-sort`), la primera columna como encabezado de fila, un `<caption>` con su nombre y un pie opcional con los totales. Se ven
 * las primeras 10 filas y «Ver todos (N)» muestra el resto. Usa las clases de `p-datatable` para tomar los estilos de Novarium
 * (cabecera, filas de 44px, pie) y así verse igual que «Quirófanos» y «Agrupado», pero con markup propio porque DataTable no
 * permite un `<caption>`.
 */
export default function OperatingRoomTable<T>({
  caption, rows, rowKey, columns, defaultSort, footerLabel, totalRows, emptyMessage,
}: OperatingRoomTableProps<T>) {
  const intl = useIntl()
  const [sort, setSort] = useState<Sort>(defaultSort)
  const [showAll, setShowAll] = useState(false)

  const sorted = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key)
    if (!column?.sortValue) return rows
    const value = column.sortValue
    return [...rows].sort((a, b) => compare(value(a), value(b), sort.order))
  }, [rows, columns, sort])

  const shown = showAll ? sorted : sorted.slice(0, TABLE_TOP_ROWS)
  const hasMore = sorted.length > TABLE_TOP_ROWS
  const total = totalRows ?? rows.length
  const truncated = total > rows.length
  const hasFooter = columns.some((column) => column.footer !== undefined)

  /** Un clic ordena por la columna (números de mayor a menor, textos de A a Z); otro clic invierte el orden. */
  const toggleSort = (column: OperatingRoomTableColumn<T>) => {
    setSort((current) => (current.key === column.key
      ? { key: column.key, order: current.order === 1 ? -1 : 1 }
      : { key: column.key, order: column.centered ? -1 : 1 }))
  }

  if (rows.length === 0) return <p className="nv-urg-empty">{emptyMessage}</p>

  return (
    <div className="nv-or-tablebox">
      <div className="nv-load-table">
        <div className="p-datatable p-component">
          <div className="p-datatable-wrapper">
            <table
              className="p-datatable-table nv-or-plain"
              style={{ minWidth: NAME_COLUMN_MIN_WIDTH + columns.reduce((sum, column) => sum + (column.width ?? 0), 0) }}
            >
              <caption className="nv-sr-only">{caption}</caption>
              <colgroup>
                {columns.map((column) => <col key={column.key} style={column.width ? { width: column.width } : undefined} />)}
              </colgroup>
              <thead className="p-datatable-thead">
                <tr>
                  {columns.map((column) => {
                    const isSorted = sort.key === column.key
                    const icon = !isSorted ? 'pi-sort-alt' : sort.order === 1 ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down'
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        className={column.centered ? 'nv-urg-center' : undefined}
                        aria-sort={isSorted ? (sort.order === 1 ? 'ascending' : 'descending') : column.sortValue ? 'none' : undefined}
                      >
                        <span className="nv-or-th">
                          {column.sortValue
                            ? (
                              <button type="button" className="nv-or-sort" onClick={() => toggleSort(column)}>
                                {column.header}
                                <i className={`pi ${icon}`} aria-hidden="true" />
                              </button>
                            )
                            : column.header}
                          {column.hint && <InfoTip text={column.hint} />}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="p-datatable-tbody">
                {shown.map((row, index) => (
                  <tr key={rowKey(row, index)}>
                    {columns.map((column, position) => {
                      if (position !== 0) return <td key={column.key} className={column.centered ? 'nv-urg-center' : undefined}>{column.cell(row)}</td>
                      // El nombre largo se corta con «…» y se lee completo en el tooltip
                      const name = column.sortValue?.(row)
                      return typeof name === 'string'
                        ? <th key={column.key} scope="row" className="nv-or-rowhead nv-tip" data-pr-tooltip={name}>{column.cell(row)}</th>
                        : <th key={column.key} scope="row" className="nv-or-rowhead">{column.cell(row)}</th>
                    })}
                  </tr>
                ))}
              </tbody>
              {hasFooter && (
                <tfoot className="p-datatable-tfoot">
                  <tr>
                    {columns.map((column, position) => (
                      <td key={column.key} className={column.centered ? 'nv-urg-center' : undefined}>
                        {position === 0 ? footerLabel : column.footer}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {(hasMore || truncated) && (
        <div className="nv-or-more">
          {hasMore && (
            <Button
              type="button"
              link
              label={intl.formatMessage(
                { id: showAll ? 'OperatingRoomIndicatorsShowLess' : 'OperatingRoomIndicatorsShowAll' },
                { count: sorted.length, countText: formatCount(intl, sorted.length), top: TABLE_TOP_ROWS },
              )}
              aria-expanded={showAll}
              onClick={() => setShowAll((value) => !value)}
            />
          )}
          {truncated && (
            <span className="nv-muted">
              {intl.formatMessage({ id: 'OperatingRoomIndicatorsTableTruncated' }, {
                countText: formatCount(intl, rows.length), totalText: formatCount(intl, total),
              })}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
